import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Scissors, Mail, Lock, User, Phone, ArrowLeft, Building2, Camera, MessageSquare, Gift, Check } from "lucide-react";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import BetaWaitlistForm from "@/components/BetaWaitlistForm";

// Phone carriers for FREE SMS notifications
const PHONE_CARRIERS = [
  { id: "att", name: "AT&T" },
  { id: "tmobile", name: "T-Mobile" },
  { id: "verizon", name: "Verizon" },
  { id: "sprint", name: "Sprint" },
  { id: "metropcs", name: "Metro PCS" },
  { id: "boost", name: "Boost Mobile" },
  { id: "cricket", name: "Cricket" },
  { id: "uscellular", name: "US Cellular" },
  { id: "virgin", name: "Virgin Mobile" },
  { id: "tracfone", name: "TracFone" },
  { id: "straight_talk", name: "Straight Talk" },
  { id: "google_fi", name: "Google Fi" },
  { id: "mint", name: "Mint Mobile" },
  { id: "visible", name: "Visible" },
  { id: "xfinity", name: "Xfinity Mobile" }
];

// Simple device fingerprint generator (basic version)
const generateFingerprint = () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('fingerprint', 2, 2);
  const canvasData = canvas.toDataURL();
  
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.colorDepth,
    screen.width + 'x' + screen.height,
    new Date().getTimezoneOffset(),
    !!window.sessionStorage,
    !!window.localStorage,
    navigator.hardwareConcurrency || 'unknown',
    canvasData.slice(-50) // Last 50 chars of canvas data
  ];
  
  // Simple hash function
  let hash = 0;
  const str = components.join('|');
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
};

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const initialRole = searchParams.get("role") || "client";
  const isLookFestEntry = searchParams.get("contest") === "lookfest";
  const defaultTrialDays = 30; // Default 30-day trial for all plans
  const hasFreeTrial = isLookFestEntry || searchParams.get("trial") !== null; // Always include trial for Look Fest
  
  // Dynamic trial days based on promo validation (soldier codes give 14 days)
  const [trialDays, setTrialDays] = useState(defaultTrialDays);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    phone_carrier: "",
    role: initialRole,
    profile_picture: "",
    sms_consent: false,
    // Look Fest fields
    instagram_handle: "",
    shop_name: "",
    shop_location: "",
    scout_code: "",
    lookfest_entry: isLookFestEntry,
    free_trial: hasFreeTrial,
    trial_days: defaultTrialDays
  });
  const [loading, setLoading] = useState(false);
  const [deviceFingerprint, setDeviceFingerprint] = useState(null);
  const [betaCheck, setBetaCheck] = useState({ loading: true, canRegister: true, message: null, waitlistEnabled: false });
  const { register } = useAuth();
  const navigate = useNavigate();

  // Generate device fingerprint on mount
  useEffect(() => {
    const fp = generateFingerprint();
    setDeviceFingerprint(fp);
  }, []);

  // Check beta registration limits
  useEffect(() => {
    const checkBetaStatus = async () => {
      try {
        const response = await axios.get(`${API}/beta/can-register/${formData.role}`);
        setBetaCheck({
          loading: false,
          canRegister: response.data.can_register,
          message: response.data.message,
          waitlistEnabled: response.data.waitlist_enabled,
          slotsAvailable: response.data.slots_available,
          betaMode: response.data.beta_mode
        });
      } catch (error) {
        setBetaCheck({ loading: false, canRegister: true, message: null, waitlistEnabled: false });
      }
    };
    checkBetaStatus();
  }, [formData.role]);

  // Update role from URL params
  useEffect(() => {
    const roleParam = searchParams.get("role");
    if (roleParam && ["client", "stylist", "owner"].includes(roleParam)) {
      setFormData(prev => ({ ...prev, role: roleParam }));
    }
  }, [searchParams]);

  // Get referral code from URL params
  const referralCode = searchParams.get("ref") || "";
  const soldierCode = searchParams.get("soldier") || searchParams.get("code") || "";
  const isBetaInvite = searchParams.get("beta") === "true";
  
  // State for promo code validation
  const [promoValidation, setPromoValidation] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  
  // Auto-populate scout_code from URL and validate
  useEffect(() => {
    if (soldierCode && !formData.scout_code) {
      setFormData(prev => ({ ...prev, scout_code: soldierCode }));
      validatePromoCode(soldierCode);
    }
  }, [soldierCode]);
  
  // Validate promo code
  const validatePromoCode = async (code) => {
    if (!code || code.length < 3) {
      setPromoValidation(null);
      setTrialDays(defaultTrialDays);
      return;
    }
    
    setValidatingPromo(true);
    try {
      const res = await axios.get(`${API}/membership/promo/validate/${code}`);
      setPromoValidation(res.data);
      
      // Update trial days if promo has extended trial (soldier codes give 14 days)
      if (res.data.valid && res.data.trial_days) {
        setTrialDays(res.data.trial_days);
        setFormData(prev => ({ ...prev, trial_days: res.data.trial_days }));
      }
    } catch (error) {
      setPromoValidation({ valid: false, message: "Could not validate code" });
      setTrialDays(defaultTrialDays);
    } finally {
      setValidatingPromo(false);
    }
  };
  
  // Debounced promo code validation when user types
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.scout_code && formData.scout_code.length >= 3) {
        validatePromoCode(formData.scout_code);
      } else {
        setPromoValidation(null);
        setTrialDays(defaultTrialDays);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.scout_code]);

  // Diverse professional images for the side panel
  const sideImages = [
    {
      src: "https://images.pexels.com/photos/7697673/pexels-photo-7697673.jpeg?auto=compress&w=800",
      alt: "Black barber giving a fresh haircut"
    },
    {
      src: "https://images.pexels.com/photos/3993462/pexels-photo-3993462.jpeg?auto=compress&w=800",
      alt: "Professional hair salon styling"
    },
    {
      src: "https://images.pexels.com/photos/897262/pexels-photo-897262.jpeg?auto=compress&w=800",
      alt: "Latino barber working on client"
    }
  ];

  // Role-specific colors and content
  const roleConfig = {
    client: {
      color: "blue",
      gradient: "from-blue-500 to-cyan-500",
      bgGradient: "from-blue-50 via-cyan-50 to-white",
      borderColor: "border-blue-200",
      title: "Book Your Next Look",
      subtitle: "Find and book appointments with top stylists"
    },
    stylist: {
      color: "orange",
      gradient: "from-orange-500 via-red-500 to-amber-500",
      bgGradient: "from-orange-50 via-amber-50 to-yellow-50",
      borderColor: "border-orange-200",
      title: "Grow Your Business",
      subtitle: "Set your services, hours, and accept payments"
    },
    owner: {
      color: "purple",
      gradient: "from-purple-500 to-indigo-500",
      bgGradient: "from-purple-50 via-indigo-50 to-white",
      borderColor: "border-purple-200",
      title: "Manage Your Salon",
      subtitle: "Collect booth rent and manage your team"
    }
  };

  const config = roleConfig[formData.role] || roleConfig.client;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Include referral code if present
      const registrationData = {
        ...formData,
        referral_code: referralCode || undefined
      };
      
      const user = await register(registrationData);
      
      // Track soldier referral if soldier code present and user is stylist
      if (soldierCode && (formData.role === 'stylist' || formData.role === 'owner')) {
        try {
          await axios.post(`${API}/soldiers/referrals/track`, {
            stylist_user_id: user.user_id,
            soldier_code: soldierCode,
            fraud_data: {
              device_fingerprint: deviceFingerprint,
              user_agent: navigator.userAgent
            }
          });
          toast.success(`Welcome to MyHairChair, ${user.name}! 🎉 Referred by a HairChair Soldier!`);
        } catch (err) {
          console.error('Failed to track soldier referral:', err);
          toast.success(`Welcome to MyHairChair, ${user.name}!`);
        }
      } else if (referralCode) {
        toast.success(`Welcome to MyHairChair, ${user.name}! 🎉 You joined via a friend's invite!`);
      } else {
        toast.success(`Welcome to MyHairChair, ${user.name}!`);
      }
      
      // Redirect based on role
      // Stylists and Owners see membership options first
      if (user.role === "owner") {
        navigate("/membership?welcome=true&role=owner");
      } else if (user.role === "stylist") {
        navigate("/membership?welcome=true&role=stylist");
      } else {
        // Clients go directly to dashboard
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    localStorage.setItem("google_signup_role", formData.role);
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const getRoleLabel = () => {
    switch(formData.role) {
      case "owner": return "Salon Owner";
      case "stylist": return "Barber/Stylist";
      default: return "Client";
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Diverse Images Gallery with gradient overlay */}
      <div className="hidden lg:block lg:w-1/2 relative">
        {/* Dynamic gradient background based on role */}
        <div className={`absolute inset-0 bg-gradient-to-br ${config.gradient} opacity-90 z-10`}></div>
        
        {/* Image collage */}
        <div className="absolute inset-0 grid grid-cols-2 gap-2 p-4 z-0">
          <div className="space-y-2">
            <div className="rounded-2xl overflow-hidden h-[55%]">
              <img 
                src={sideImages[0].src}
                alt={sideImages[0].alt}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden h-[43%]">
              <img 
                src={sideImages[2].src}
                alt={sideImages[2].alt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="pt-8">
            <div className="rounded-2xl overflow-hidden h-[90%]">
              <img 
                src={sideImages[1].src}
                alt={sideImages[1].alt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Content overlay */}
        <div className="absolute inset-0 flex items-center justify-center z-20 p-12">
          <div className="text-center text-white">
            <h2 className="font-display text-4xl font-bold mb-4 drop-shadow-lg">{config.title}</h2>
            <p className="text-white/90 text-lg max-w-md drop-shadow-md">
              {config.subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className={`flex-1 flex flex-col justify-center px-4 sm:px-8 py-8 sm:py-12 lg:px-16 overflow-y-auto bg-gradient-to-br ${config.bgGradient}`}>
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 sm:mb-8 transition-colors text-sm sm:text-base">
            <ArrowLeft className="w-4 h-4" />
            Back to portal
          </Link>

          <div className="flex items-center gap-2 mb-6 sm:mb-8">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br ${config.gradient} rounded-xl flex items-center justify-center shadow-lg`}>
              <Scissors className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <span className="font-display font-bold text-lg sm:text-xl text-gray-800">MyHairChair</span>
          </div>

          {/* Beta Full - Show Waitlist Form */}
          {!betaCheck.loading && !betaCheck.canRegister && betaCheck.waitlistEnabled && (formData.role === "stylist" || formData.role === "owner") ? (
            <BetaWaitlistForm 
              role={formData.role} 
              onSuccess={() => toast.success("You're on the waitlist!")} 
            />
          ) : (
            <>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
                Create your account
              </h1>
              <p className="text-gray-500 mb-4 text-sm sm:text-base">
                Get started as a <span className="font-medium">{getRoleLabel()}</span>
              </p>
              
              {/* Beta Invite Badge */}
              {isBetaInvite && referralCode && (
                <div className="mb-6 p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl">
                  <div className="flex items-center gap-2 text-purple-700">
                    <span className="text-lg">🎉</span>
                    <p className="text-sm font-medium">You&apos;ve been invited to the MyHairChair Beta!</p>
                  </div>
                  <p className="text-xs text-purple-600/80 mt-1">Join now to get early access to all features</p>
                </div>
              )}

              {/* Beta Slots Available Badge */}
              {betaCheck.betaMode && betaCheck.slotsAvailable !== null && betaCheck.slotsAvailable > 0 && betaCheck.slotsAvailable <= 10 && (formData.role === "stylist" || formData.role === "owner") && (
                <div className="mb-6 p-4 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/30 rounded-xl">
                  <div className="flex items-center gap-2 text-amber-700">
                    <span className="text-lg">⚡</span>
                    <p className="text-sm font-medium">Only {betaCheck.slotsAvailable} beta spots remaining!</p>
                  </div>
                  <p className="text-xs text-amber-600/80 mt-1">Sign up now to secure your spot</p>
                </div>
              )}

              {/* Role Selection */}
          <div className={`mb-6 sm:mb-8 p-4 sm:p-6 bg-white/80 backdrop-blur-sm rounded-2xl border ${config.borderColor} shadow-sm`}>
            <Label className="text-gray-700 mb-3 sm:mb-4 block text-base sm:text-lg font-medium">I am a...</Label>
            <RadioGroup 
              value={formData.role} 
              onValueChange={(value) => setFormData({ ...formData, role: value })}
              className="grid grid-cols-3 gap-2 sm:gap-3"
            >
              <div 
                className={`flex flex-col items-center p-2.5 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  formData.role === "client" 
                    ? "border-blue-400 bg-gradient-to-br from-blue-50 to-cyan-50" 
                    : "border-gray-200 hover:border-blue-200 bg-white"
                }`}
                onClick={() => setFormData({ ...formData, role: "client" })}
              >
                <RadioGroupItem value="client" id="client" className="sr-only" data-testid="role-client-radio" />
                <User className={`w-5 h-5 sm:w-7 sm:h-7 mb-1 sm:mb-2 ${formData.role === "client" ? "text-blue-500" : "text-gray-400"}`} />
                <Label htmlFor="client" className={`font-medium cursor-pointer text-xs sm:text-sm ${formData.role === "client" ? "text-gray-800" : "text-gray-600"}`}>
                  Client
                </Label>
              </div>
              <div 
                className={`flex flex-col items-center p-2.5 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  formData.role === "stylist" 
                    ? "border-orange-400 bg-gradient-to-br from-orange-50 to-amber-50" 
                    : "border-gray-200 hover:border-orange-200 bg-white"
                }`}
                onClick={() => setFormData({ ...formData, role: "stylist" })}
              >
                <RadioGroupItem value="stylist" id="stylist" className="sr-only" data-testid="role-stylist-radio" />
                <Scissors className={`w-5 h-5 sm:w-7 sm:h-7 mb-1 sm:mb-2 ${formData.role === "stylist" ? "text-orange-500" : "text-gray-400"}`} />
                <Label htmlFor="stylist" className={`font-medium cursor-pointer text-xs sm:text-sm ${formData.role === "stylist" ? "text-gray-800" : "text-gray-600"}`}>
                  Stylist
                </Label>
              </div>
              <div 
                className={`flex flex-col items-center p-2.5 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  formData.role === "owner" 
                    ? "border-purple-400 bg-gradient-to-br from-purple-50 to-indigo-50" 
                    : "border-gray-200 hover:border-purple-200 bg-white"
                }`}
                onClick={() => setFormData({ ...formData, role: "owner" })}
              >
                <RadioGroupItem value="owner" id="owner" className="sr-only" data-testid="role-owner-radio" />
                <Building2 className={`w-5 h-5 sm:w-7 sm:h-7 mb-1 sm:mb-2 ${formData.role === "owner" ? "text-purple-500" : "text-gray-400"}`} />
                <Label htmlFor="owner" className={`font-medium cursor-pointer text-xs sm:text-sm ${formData.role === "owner" ? "text-gray-800" : "text-gray-600"}`}>
                  Owner
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Google Sign Up Button */}
          <Button
            type="button"
            variant="outline"
            className={`w-full h-11 sm:h-12 gap-2 sm:gap-3 mb-4 sm:mb-6 ${config.borderColor} hover:bg-white bg-white/80 text-sm sm:text-base`}
            onClick={handleGoogleSignup}
            data-testid="google-signup-btn"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="truncate">Continue with Google as {getRoleLabel()}</span>
          </Button>

          <div className="relative mb-4 sm:mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className={`w-full border-t ${config.borderColor}`}></div>
            </div>
            <div className="relative flex justify-center text-xs sm:text-sm">
              <span className={`px-4 bg-gradient-to-r ${config.bgGradient} text-gray-400`}>Or register with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="name" className="text-gray-700 text-sm sm:text-base">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400 pointer-events-none z-10" />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  className={`!pl-10 sm:!pl-11 h-11 sm:h-12 text-sm sm:text-base ${config.borderColor} bg-white/80`}
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="email" className="text-gray-700 text-sm sm:text-base">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400 pointer-events-none z-10" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className={`!pl-10 sm:!pl-11 h-11 sm:h-12 text-sm sm:text-base ${config.borderColor} bg-white/80`}
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="phone" className="text-gray-700 text-sm sm:text-base flex items-center gap-1 sm:gap-2 flex-wrap">
                <span>Phone</span>
                <span className="text-xs text-green-600 font-normal whitespace-nowrap">(Get FREE SMS reminders!)</span>
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400 pointer-events-none z-10" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`!pl-10 sm:!pl-11 h-11 sm:h-12 text-sm sm:text-base ${config.borderColor} bg-white/80`}
                  data-testid="register-phone-input"
                />
              </div>
            </div>

            {/* Phone Carrier for FREE SMS - only show if phone is entered */}
            {formData.phone && (
              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="phone_carrier" className="text-gray-700 text-sm sm:text-base flex items-center gap-1 sm:gap-2 flex-wrap">
                  <MessageSquare className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>Phone Carrier</span>
                  <span className="text-xs text-green-600 font-normal whitespace-nowrap">(for FREE text notifications)</span>
                </Label>
                <Select
                  value={formData.phone_carrier}
                  onValueChange={(value) => setFormData({ ...formData, phone_carrier: value })}
                >
                  <SelectTrigger className={`h-11 sm:h-12 text-sm sm:text-base ${config.borderColor} bg-white/80`} data-testid="carrier-select">
                    <SelectValue placeholder="Select your phone carrier" />
                  </SelectTrigger>
                  <SelectContent>
                    {PHONE_CARRIERS.map((carrier) => (
                      <SelectItem key={carrier.id} value={carrier.id}>
                        {carrier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  📱 We&apos;ll send appointment reminders via text - 100% FREE!
                </p>
              </div>
            )}

            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="password" className="text-gray-700 text-sm sm:text-base">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400 pointer-events-none z-10" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className={`!pl-10 sm:!pl-11 h-11 sm:h-12 text-sm sm:text-base ${config.borderColor} bg-white/80`}
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
              </div>
            </div>

            {/* Profile Picture URL for clients */}
            {formData.role === "client" && (
              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="profile_picture" className="text-gray-700 text-sm sm:text-base">Profile Picture URL (optional)</Label>
                <div className="relative">
                  <Camera className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400 pointer-events-none z-10" />
                  <Input
                    id="profile_picture"
                    name="profile_picture"
                    type="url"
                    placeholder="https://example.com/your-photo.jpg"
                    value={formData.profile_picture}
                    onChange={handleChange}
                    className={`!pl-10 sm:!pl-11 h-11 sm:h-12 text-sm sm:text-base ${config.borderColor} bg-white/80`}
                    data-testid="register-profile-picture-input"
                  />
                </div>
              </div>
            )}

            {/* Look Fest Contest Entry Fields */}
            {isLookFestEntry && (formData.role === "stylist" || formData.role === "owner") && (
              <div className="space-y-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                <div className="flex items-center gap-2 text-amber-700 mb-2">
                  <span className="text-xl">🏆</span>
                  <p className="font-bold">Keep the 100 Look Fest Entry</p>
                </div>
                
                <div className="bg-green-100 border border-green-300 rounded-lg p-3 text-sm text-green-700">
                  <span className="font-bold">✨ {trialDays}-Day FREE Premium Trial Included!</span> Experience all features.
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="instagram_handle" className="text-gray-700 text-sm flex items-center gap-2">
                    <svg className="w-4 h-4 text-pink-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    Instagram Handle <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="instagram_handle"
                    name="instagram_handle"
                    type="text"
                    placeholder="@yourusername"
                    value={formData.instagram_handle}
                    onChange={handleChange}
                    className="h-11 text-sm border-amber-200 bg-white"
                    required={isLookFestEntry}
                    data-testid="lookfest-instagram-input"
                  />
                  <p className="text-xs text-gray-500">Post your looks to <span className="font-bold text-pink-600">@myhairchairhq</span> with <span className="font-bold text-amber-600">#KeepThe100LookFest</span></p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="shop_name" className="text-gray-700 text-sm">
                    Shop/Salon Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="shop_name"
                    name="shop_name"
                    type="text"
                    placeholder="Your salon or shop name"
                    value={formData.shop_name}
                    onChange={handleChange}
                    className="h-11 text-sm border-amber-200 bg-white"
                    required={isLookFestEntry}
                    data-testid="lookfest-shop-name-input"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="shop_location" className="text-gray-700 text-sm">
                    Location (City, State) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="shop_location"
                    name="shop_location"
                    type="text"
                    placeholder="Houston, TX"
                    value={formData.shop_location}
                    onChange={handleChange}
                    className="h-11 text-sm border-amber-200 bg-white"
                    required={isLookFestEntry}
                    data-testid="lookfest-location-input"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="scout_code" className="text-gray-700 text-sm flex items-center gap-2">
                    <Gift className="w-4 h-4 text-amber-500" />
                    Soldier Promo Code <span className="text-gray-400 font-normal">(optional)</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="scout_code"
                      name="scout_code"
                      type="text"
                      placeholder="Enter code for special pricing"
                      value={formData.scout_code}
                      onChange={handleChange}
                      className={`h-11 text-sm uppercase ${
                        promoValidation?.valid 
                          ? 'border-green-400 bg-green-50' 
                          : promoValidation?.valid === false 
                            ? 'border-red-300 bg-red-50' 
                            : 'border-amber-200 bg-white'
                      }`}
                      data-testid="lookfest-scout-code-input"
                    />
                    {validatingPromo && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                    {promoValidation?.valid && !validatingPromo && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Check className="w-5 h-5 text-green-500" />
                      </div>
                    )}
                  </div>
                  
                  {/* Promo code validation result */}
                  {promoValidation?.valid && (
                    <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                      <p className="text-green-700 text-sm font-medium flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        {promoValidation.message}
                      </p>
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-green-600 font-medium">🎉 Special Pricing Unlocked:</p>
                        <div className="flex gap-4 text-xs">
                          <span className="bg-white px-2 py-1 rounded border border-green-200">
                            <strong>Pro:</strong> ${promoValidation.promo_pricing?.pro?.intro_price}/mo for 3 months
                          </span>
                          <span className="bg-white px-2 py-1 rounded border border-green-200">
                            <strong>Elite:</strong> ${promoValidation.promo_pricing?.elite?.intro_price}/mo for 3 months
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {promoValidation?.valid === false && (
                    <p className="text-xs text-red-500">{promoValidation.message}</p>
                  )}
                  
                  {!promoValidation && (
                    <p className="text-xs text-gray-500">
                      Got a code from a MyHairChair Soldier? Enter it for promotional pricing!
                    </p>
                  )}
                </div>

                <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 text-sm text-purple-700">
                  <span className="font-bold">📸 Post Unlimited Looks!</span> The more you post to @myhairchairhq, the more chances to win!
                </div>
              </div>
            )}

            {/* Look Fest Entry Notice for Clients */}
            {isLookFestEntry && formData.role === "client" && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-700 text-sm font-medium">
                  ⚠️ The Look Fest contest is only open to Stylists, Barbers, and Salon Owners.
                </p>
                <p className="text-red-600 text-xs mt-1">
                  Please select "Stylist" or "Owner" to enter the contest.
                </p>
              </div>
            )}

            {/* SMS Consent Checkbox - StyleSeat style */}
            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="sms_consent"
                  checked={formData.sms_consent}
                  onCheckedChange={(checked) => setFormData({ ...formData, sms_consent: checked })}
                  className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                  data-testid="sms-consent-checkbox"
                />
                <Label 
                  htmlFor="sms_consent" 
                  className="text-sm text-gray-700 font-normal leading-snug cursor-pointer"
                >
                  Yes, I would like to receive SMS notifications from MyHairChair
                </Label>
              </div>
              
              {/* SMS Consent Disclaimer */}
              <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500 leading-relaxed">
                <p>
                  *By submitting your phone number, you agree to receive recurring automated account and marketing text messages from MyHairChair at the cell number used when signing up. Consent is not a condition of sign up. Reply <span className="font-medium">HELP</span> for help and <span className="font-medium">STOP</span> to cancel. Msg frequency varies. Msg and data rates may apply. View our{" "}
                  <Link to="/legal/terms" className="text-gray-700 underline hover:text-gray-900">Terms of Service</Link>
                  {" "}and{" "}
                  <Link to="/legal/privacy" className="text-gray-700 underline hover:text-gray-900">Privacy Policy</Link>.
                </p>
              </div>
            </div>

            <Button 
              type="submit" 
              className={`bg-gradient-to-r ${config.gradient} hover:opacity-90 text-white w-full h-11 sm:h-12 rounded-xl shadow-lg text-sm sm:text-base`}
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? "Creating account..." : `Create ${getRoleLabel()} Account`}
            </Button>
          </form>

          <p className="mt-4 sm:mt-6 text-center text-xs sm:text-sm text-gray-400">
            By creating an account, you agree to our{" "}
            <Link to="/legal/terms" className="text-gray-600 hover:text-gray-700">Terms of Service</Link>
            {" "}and{" "}
            <Link to="/legal/privacy" className="text-gray-600 hover:text-gray-700">Privacy Policy</Link>
          </p>

          <p className="mt-6 sm:mt-8 text-center text-gray-600 text-sm sm:text-base">
            Already have an account?{" "}
            <Link to="/login" className="text-gray-800 font-medium hover:underline">
              Sign in
            </Link>
          </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
